import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { WACOM_VENDOR_ID, isWacomDevice } from './wacom/wacomConstants';
import { setInkingMode, sendImageToSTU, renderWallpaperImage } from './wacom/wacomProtocol';

export const WacomContext = createContext({
    wacomConnected: false,
    wacomDeviceName: '',
    wacomDevice: null,
    requestConnection: async () => {},
    isSupported: false,
    registerActiveModal: () => () => {},
});

export const WacomProvider = ({ children }) => {
    const [wacomConnected, setWacomConnected] = useState(false);
    const [wacomDeviceName, setWacomDeviceName] = useState('');
    const deviceRef = useRef(null);
    const wacomEverSeen = useRef(false);
    const activeModalCount = useRef(0);
    const busyRef = useRef(false);
    const isSupported = typeof navigator !== 'undefined' && !!navigator.hid;

    // ── Wallpaper en idle ───────────────────────────────────────────
    const sendWallpaperIfIdle = useCallback(async (device, attempt = 0, skipReopen = false) => {
        if (activeModalCount.current > 0) return;
        if (!device) return;
        if (busyRef.current) return;
        busyRef.current = true;
        try {
            // Cerrar y reabrir si el handle está viejo (previene NotAllowedError)
            // skipReopen=true cuando venimos de cerrar un modal (handle fresco)
            if (!skipReopen && device.opened && attempt === 0) {
                try { await device.close(); } catch (_) {}
                await new Promise(r => setTimeout(r, 300));
            }
            if (!device.opened) {
                await device.open();
                await new Promise(r => setTimeout(r, 500));
            }
            await setInkingMode(device, false);
            const wallpaper = await renderWallpaperImage();
            await sendImageToSTU(device, wallpaper);
            await setInkingMode(device, false);
        } catch (e) {
            console.warn('WacomProvider: error enviando wallpaper (intento ' + attempt + ')', e);
            if (attempt < 1) {
                try { if (device.opened) await device.close(); } catch (_) {}
                await new Promise(r => setTimeout(r, 1000));
                busyRef.current = false;
                return sendWallpaperIfIdle(device, attempt + 1);
            }
        } finally {
            busyRef.current = false;
        }
    }, []);

    // ── Detección inicial + listeners connect/disconnect ────────────
    useEffect(() => {
        if (!isSupported) return;

        const checkDevices = async () => {
            try {
                const devices = await navigator.hid.getDevices();
                const wacom = devices.find(isWacomDevice);
                if (wacom) {
                    wacomEverSeen.current = true;
                    deviceRef.current = wacom;
                    setWacomConnected(true);
                    setWacomDeviceName(wacom.productName || 'Wacom STU-540');
                    sendWallpaperIfIdle(wacom);
                }
            } catch (_) {}
        };

        checkDevices();

        const onConnect = (e) => {
            if (isWacomDevice(e.device) && !busyRef.current) {
                wacomEverSeen.current = true;
                deviceRef.current = e.device;
                setWacomConnected(true);
                setWacomDeviceName(e.device.productName || 'Wacom STU-540');
                sendWallpaperIfIdle(e.device);
            }
        };

        const onDisconnect = (e) => {
            if (isWacomDevice(e.device) && !busyRef.current) {
                deviceRef.current = null;
                setWacomConnected(false);
                setWacomDeviceName('');
            }
        };

        navigator.hid.addEventListener('connect', onConnect);
        navigator.hid.addEventListener('disconnect', onDisconnect);

        return () => {
            navigator.hid.removeEventListener('connect', onConnect);
            navigator.hid.removeEventListener('disconnect', onDisconnect);
        };
    }, [isSupported, sendWallpaperIfIdle]);

    // ── Polling periódico para recuperar conexión perdida ───────────
    useEffect(() => {
        if (!isSupported) return;

        const interval = setInterval(async () => {
            if (activeModalCount.current > 0) return;
            try {
                const devices = await navigator.hid.getDevices();
                const wacom = devices.find(isWacomDevice);
                if (wacom && !deviceRef.current) {
                    wacomEverSeen.current = true;
                    deviceRef.current = wacom;
                    setWacomConnected(true);
                    setWacomDeviceName(wacom.productName || 'Wacom STU-540');
                    sendWallpaperIfIdle(wacom);
                } else if (!wacom && deviceRef.current) {
                    deviceRef.current = null;
                    setWacomConnected(false);
                    setWacomDeviceName('');
                }
            } catch (_) {}
        }, 8000);

        return () => clearInterval(interval);
    }, [isSupported, sendWallpaperIfIdle]);

    // ── Solicitud manual de conexión (requiere gesto del usuario) ───
    const requestConnection = useCallback(async () => {
        if (!isSupported) return;
        try {
            const devices = await navigator.hid.requestDevice({
                filters: [{ vendorId: WACOM_VENDOR_ID }],
            });
            if (devices.length > 0) {
                wacomEverSeen.current = true;
                deviceRef.current = devices[0];
                setWacomConnected(true);
                setWacomDeviceName(devices[0].productName || 'Wacom STU-540');
                sendWallpaperIfIdle(devices[0]);
            }
        } catch (_) {}
    }, [isSupported, sendWallpaperIfIdle]);

    // ── registerActiveModal: los modales de firma llaman esto ────────
    // Devuelve una función de unregister. Cuando el count vuelve a 0,
    // se envía el wallpaper automáticamente.
    const registerActiveModal = useCallback(() => {
        activeModalCount.current += 1;
        return () => {
            activeModalCount.current -= 1;
            if (activeModalCount.current === 0 && deviceRef.current) {
                busyRef.current = false;
                sendWallpaperIfIdle(deviceRef.current, 0, true);
            }
        };
    }, [sendWallpaperIfIdle]);

    // ── Cleanup al salir de la página ───────────────────────────────
    useEffect(() => {
        const closeDevice = () => {
            const dev = deviceRef.current;
            if (dev?.opened) {
                deviceRef.current = null;
                try { dev.close(); } catch (_) {}
            }
        };

        window.addEventListener('beforeunload', closeDevice);
        return () => {
            window.removeEventListener('beforeunload', closeDevice);
            const dev = deviceRef.current;
            if (dev?.opened) {
                deviceRef.current = null;
                (async () => {
                    try {
                        await setInkingMode(dev, false);
                    } catch (_) {}
                    try { await dev.close(); } catch (_) {}
                })();
            }
        };
    }, []);

    return (
        <WacomContext.Provider value={{
            wacomConnected,
            wacomDeviceName,
            wacomDevice: deviceRef.current,
            requestConnection,
            isSupported,
            registerActiveModal,
        }}>
            {children}
        </WacomContext.Provider>
    );
};
